import { useDispatch } from "react-redux";
import { ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { clearFilters } from "redux/SingleDataset/actions";

import { AMP_DATASET_EXPLORATION_EVENT__TAB_CHANGED } from "helpers/constants/amplitudeEvents";
import { AMP_DATASET_EXPLORATION_PROPERTY__TAB_NAME } from "helpers/constants/amplitudeProperties";

import { DatasetStatusEnum, TabTypes } from "types";

import ExploreTabIcon from "assets/icons/ExploreTabIcon";
import EnrichmentIcon from "assets/icons/EnrichmentIcon";
import DataTabIcon from "assets/icons/DataTabIcon";
import classNames from "classnames";
import styles from "./style.module.scss";
import { FaRegStar } from "react-icons/fa";

interface TabProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon: ReactNode;
  showBeta?: boolean;
  disabled: boolean;
}

interface TabSwitchProps {
  showTabs: TabTypes[];
  dataset: any;
}

const TABS = [
  {
    type: TabTypes.DATA_INGESTION,
    label: "Data",
    icon: <DataTabIcon />,
  },
  {
    type: TabTypes.DATA_EXPLORATION,
    label: "Explore",
    icon: <ExploreTabIcon />,
  },
  {
    type: TabTypes.DATA_ENRICHMENT,
    label: "Enrich",
    icon: <EnrichmentIcon />,
  },
  { type: TabTypes.SAVED_VIEWS, label: "Views", icon: <FaRegStar /> },
];

const Tab = ({ icon, label, onClick, disabled, selected }: TabProps) => {
  const tabButtonContainer = classNames(styles.tabButton, {
    [styles.selected]: selected,
    [styles.disableTab]: disabled,
  });

  return (
    <div className={tabButtonContainer} onClick={onClick}>
      {icon}
      {label}
      {selected && <div className={styles.selectedBorderBottom}></div>}
    </div>
  );
};

const TabSwitch = ({ showTabs, dataset }: TabSwitchProps) => {
  const { datasetId, newDatasetId, enrichDatasetId, viewsDatasetId } =
    useParams();

  const initialTab = enrichDatasetId
    ? TabTypes.DATA_ENRICHMENT
    : newDatasetId
    ? TabTypes.DATA_INGESTION
    : viewsDatasetId
    ? TabTypes.SAVED_VIEWS
    : TabTypes.DATA_EXPLORATION;

  const [selectedTab, setSelectedTab] = useState<TabTypes>(initialTab); //does not contain the BOTH value

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handlePageClick = (value: TabTypes) => {
    if (selectedTab === value) return;
    const id = newDatasetId || enrichDatasetId || datasetId || viewsDatasetId;
    setSelectedTab(value);
    switch (value) {
      case TabTypes.DATA_EXPLORATION:
        navigate(`/dataset/${id}/data`);
        break;

      case TabTypes.DATA_INGESTION:
        dispatch(clearFilters() as any);
        navigate(`/dataset/${id}/create`);
        break;

      case TabTypes.DATA_ENRICHMENT:
        dispatch(clearFilters() as any);
        navigate(`/dataset/${id}/enrich`);
        break;

      case TabTypes.SAVED_VIEWS:
        dispatch(clearFilters() as any);
        navigate(`/dataset/${id}/views`);
        break;
    }

    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__TAB_CHANGED, {
      [AMP_DATASET_EXPLORATION_PROPERTY__TAB_NAME]: value,
    });
  };

  const filteredTabs = TABS.filter((tab) => showTabs.includes(tab.type));

  return (
    <div className={styles.pageSwitchContainer}>
      <div className={styles.tabContainer}>
        {filteredTabs.map((tab) => {
          const selected = selectedTab === tab.type;
          const disabled = () => {
            if (
              tab.type in [TabTypes.DATA_ENRICHMENT, TabTypes.DATA_EXPLORATION]
            ) {
              return dataset?.status !== DatasetStatusEnum.READY;
            } else {
              return false;
            }
          };
          return (
            <Tab
              key={tab.type}
              label={tab.label}
              selected={selected}
              disabled={disabled()}
              onClick={() => handlePageClick(tab.type)}
              icon={tab.icon}
            />
          );
        })}
      </div>
    </div>
  );
};

export default TabSwitch;
